// Runtime-config store (§9.3 boot step 1, §6.4) — populated from
// `GET /bff/runtime-config` before the router/app mounts. That BFF
// controller doesn't exist yet (later phase, backend-developer); `load()`
// falls back to a safe local default on any fetch failure (network error,
// 404 in Phase 0, non-2xx, or malformed JSON) so `pnpm --filter
// @eskayscience/web run dev` renders correctly today and needs zero code
// changes once the real endpoint lands.
import { defineStore } from 'pinia';

export interface RuntimeConfig {
  appName: string;
  /** Base URL for `/v1` API calls — relative in production (single-origin, §11), absolute in local dev if ever needed. */
  apiBase: string;
  /** CSS custom-property tokens applied to `:root` at boot (e.g. `{ "color-primary": "#2563eb" }`). */
  theme: Record<string, string>;
  featureFlags: Record<string, boolean>;
  sentryDsn: string | null;
  defaultLocale: string;
  authMode: 'password' | 'sso';
  mediaCdnHost: string | null;
  /**
   * Cloudflare Turnstile "site key" for the registration-page captcha widget
   * (AUTH-01 register-captcha). Public by design (paired with a server-only
   * secret key verified in `apps/api`), sourced entirely from this runtime
   * endpoint so the SPA can be built once and deployed unchanged across
   * environments (dev/staging/prod each set their own `TURNSTILE_SITE_KEY`
   * Worker var). Empty string until the real config lands or on any fetch
   * failure — components must treat an empty value as "not ready yet" and
   * withhold the widget rather than rendering it with a blank sitekey.
   */
  turnstileSiteKey: string;
  /**
   * Off-by-default kill-switch for the whole Turnstile-captcha-on-register
   * feature (2026-08-29 follow-up, PO ruling: no real production
   * sitekey/secret exists yet). `false`/absent means components must NOT
   * render the widget and must NOT require a token before submit —
   * registration behaves exactly as it did before this feature existed.
   * Mirrors the API's `Env.TURNSTILE_ENABLED` flag 1:1.
   */
  turnstileEnabled: boolean;
}

export const FALLBACK_RUNTIME_CONFIG: RuntimeConfig = {
  appName: 'Eskay Science',
  apiBase: '/v1',
  theme: {},
  featureFlags: {},
  sentryDsn: null,
  defaultLocale: 'en',
  authMode: 'password',
  mediaCdnHost: null,
  turnstileSiteKey: '',
  turnstileEnabled: false,
};

export const useConfigStore = defineStore('config', {
  state: () => ({
    runtimeConfig: { ...FALLBACK_RUNTIME_CONFIG } as RuntimeConfig,
    loaded: false,
    usedFallback: false,
  }),
  actions: {
    async load(): Promise<void> {
      try {
        const res = await fetch('/bff/runtime-config', { headers: { Accept: 'application/json' } });
        if (!res.ok) throw new Error(`GET /bff/runtime-config -> ${res.status}`);
        const data = (await res.json()) as Partial<RuntimeConfig>;
        this.runtimeConfig = { ...FALLBACK_RUNTIME_CONFIG, ...data };
        this.usedFallback = false;
      } catch (err) {
        // Expected in Phase 0 (route doesn't exist yet) and in any
        // genuinely offline/degraded scenario — never blocks boot.

        console.warn('[config] /bff/runtime-config unavailable, using local fallback:', err);
        this.runtimeConfig = { ...FALLBACK_RUNTIME_CONFIG };
        this.usedFallback = true;
      } finally {
        this.loaded = true;
      }
    },

    applyThemeTokens(): void {
      for (const [key, value] of Object.entries(this.runtimeConfig.theme)) {
        // `dataTheme` is not a CSS custom property — it selects the light/dark
        // token set (see styles/tokens.css, keyed by `data-theme`). Runtime
        // config wins over the OS/localStorage default on boot; the useTheme
        // composable reads back from `documentElement.dataset.theme`.
        if (key === 'dataTheme') {
          if (value === 'light' || value === 'dark') {
            document.documentElement.dataset.theme = value;
          }
          continue;
        }
        // Every other token is written INLINE on <html>, which outranks the
        // `:root` defaults in tokens.css — so a runtime-config `acc` override
        // cascades straight through the `bg-acc`/`text-acc` utilities.
        document.documentElement.style.setProperty(`--${key}`, value);
      }
      document.title = this.runtimeConfig.appName;
    },
  },
});
